import fetchWithAuth from "@/api/secureFetch";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "";

export interface InitiateGoogleDriveOAuthRequest {
    userId?: number;
    scope?: string;
    return_to?: string;
}

export interface InitiateGoogleDriveOAuthResponse {
    authUrl?: string;
    authorizationUrl?: string;
    state?: string;
}

export interface GoogleDriveEntry {
    ref: string;
    name: string;
    is_folder?: boolean;
    size?: number;
}

export interface GoogleDriveBrowseResponse {
    entries: GoogleDriveEntry[];
    next_page_token?: string | null;
}

export interface GoogleDriveFolder {
    id: string;
    name: string;
}

export interface GoogleDrivePickerSession {
    accessToken: string;
    clientId?: string | null;
}

export async function initiateGoogleDriveOAuth(
    request: InitiateGoogleDriveOAuthRequest,
    token: string
): Promise<InitiateGoogleDriveOAuthResponse> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/v1/storage/google-drive/oauth/authorize`, token, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(request)
    });
    if (!response.ok) {
        throw new Error(`Failed to initiate OAuth: ${response.status}`);
    }
    return response.json();
}

export async function getGoogleDriveStatus(
    token: string
): Promise<{ connected: boolean; account?: { email?: string; name?: string } | null }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/v1/storage/google-drive/status`, token, {
        method: "GET"
    });
    if (!response.ok) {
        throw new Error(`Failed to fetch Google Drive status: ${response.status}`);
    }
    return response.json();
}

export async function browseGoogleDrive(
    token: string,
    ref?: string,
    pageToken?: string
): Promise<GoogleDriveBrowseResponse> {
    const params = new URLSearchParams();
    if (ref) params.set("ref", ref);
    if (pageToken) params.set("page_token", pageToken);
    const suffix = params.toString() ? `?${params.toString()}` : "";
    const response = await fetchWithAuth(`${API_BASE_URL}/api/v1/storage/google-drive/browse${suffix}`, token, {
        method: "GET"
    });
    if (!response.ok) {
        throw new Error(`Failed to browse Google Drive: ${response.status}`);
    }
    return response.json();
}

export async function listGoogleDriveFolders(token: string): Promise<{ folders: GoogleDriveFolder[] }> {
    const data = await browseGoogleDrive(token);
    return {
        folders: (data.entries || [])
            .filter(entry => Boolean(entry.is_folder))
            .map(entry => ({ id: entry.ref, name: entry.name }))
    };
}

export async function getGoogleDrivePickerSession(token: string): Promise<GoogleDrivePickerSession> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/v1/storage/google-drive/picker/session`, token, {
        method: "GET"
    });
    if (!response.ok) {
        throw new Error(`Failed to initialize Google Picker: ${response.status}`);
    }
    return response.json();
}

export interface GoogleDriveImportResult {
    ref: string;
    filename?: string | null;
    status: "imported" | "existing" | "error";
    fileid?: number;
    error?: string;
}

export interface GoogleDriveImportResponse {
    imported: number;
    existing?: number;
    failed: number;
    results?: GoogleDriveImportResult[];
}

export async function importGoogleDriveRefs(
    refs: Array<string | Pick<GoogleDriveEntry, "ref" | "name" | "size" | "is_folder">>,
    token: string
): Promise<GoogleDriveImportResponse> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/v1/storage/google-drive/import`, token, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refs })
    });
    if (!response.ok) {
        throw new Error(`Failed to import Google Drive refs: ${response.status}`);
    }
    return response.json();
}

export async function downloadGoogleDriveRef(
    ref: string,
    token: string
): Promise<{ blob: Blob; filename: string }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/v1/storage/google-drive/download`, token, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ref })
    });
    if (!response.ok) {
        throw new Error(`Failed to download Google Drive file: ${response.status}`);
    }
    const blob = await response.blob();
    const disposition = response.headers.get("content-disposition") || "";
    const match = disposition.match(/filename="?([^";]+)"?/i);
    const filename = match?.[1]?.trim() || "download";
    return { blob, filename };
}

export async function disconnectGoogleDrive(token: string): Promise<{ success: boolean }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/v1/storage/google-drive/disconnect`, token, {
        method: "DELETE"
    });
    if (!response.ok) {
        throw new Error(`Failed to disconnect Google Drive: ${response.status}`);
    }
    return response.json();
}
